'use client';

import type { ReactNode } from 'react';
import type { Zone } from '@/types/zones/zone';

import { useMemo, useState } from 'react';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { MultiZoneTabButton } from './subcomponents/multi-zone-tab-button';
import { getText } from '@/utils/text/text';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { CategoryViewAll } from '@/app/elements/category-view-all';
import { useGlobalContext } from '@/store/contexts/global-context';
import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';

export type MultiZoneClientProps = {
    children: ReactNode[];
    zones: Zone[];
    selectedChildZoneUid?: string;
};

export const MultiZoneClient: React.FC<MultiZoneClientProps> = ({ children, zones, selectedChildZoneUid = null }) => {
    const [selectedZoneUid, setSelectedZoneUid] = useState(selectedChildZoneUid);
    const { isMobile } = useGlobalContext();

    const selectedElementIndex = zones.findIndex((z) => z.uid === selectedZoneUid);
    const selectedZone = useMemo(() => zones.find((zone) => zone.uid === selectedZoneUid), [selectedZoneUid]);

    const isZoneEmpty = !(selectedZone?.data?.body?.[0]?.items ?? [])?.length;
    const categoryUid = selectedZone?.data?.body?.[0]?.primary?.category?.uid;

    const handleTabClick = (zoneUid: string) => {
        if (selectedZoneUid === zoneUid) {
            return;
        }

        setSelectedZoneUid(zoneUid);
    };

    const renderEmptyItemsMessage = () => {
        if (!selectedZone?.data?.title) {
            return null;
        }

        return (
            <div data-testid="empty-zone" className="multi-zone__empty-zone-message">
                {getText({
                    text: 'components.multiZone.noZoneItems',
                    params: [selectedZone?.data.title[0].text.toLowerCase()]
                })}
            </div>
        );
    };

    return (
        <div className="multi-zone wrapper" data-t-zonemodule="atf_module">
            <div className="multi-zone__tab-wrapper">
                <div className="multi-zone__tab-wrapper__tab-container">
                    {zones.map((z: Zone) => (
                        <MultiZoneTabButton
                            key={z?.uid}
                            title={z?.data?.title?.[0]?.text ?? ''}
                            icon={z?.icon as any}
                            isSelected={z?.uid === selectedZoneUid}
                            onClick={() => handleTabClick(z?.uid)}
                        />
                    ))}
                </div>
                {categoryUid && !isMobile && (
                    <CategoryViewAll categoryUid={categoryUid} zoneName={selectedZone?.data?.title?.[0]?.text ?? ''} />
                )}
            </div>
            {isZoneEmpty ? (
                renderEmptyItemsMessage()
            ) : (
                <div
                    className="child-zone-wrapper"
                    data-t-zonename={selectedZone?.data?.title?.[0]?.text ?? ''}
                    data-t-dynamicordering={selectedZone?.data?.dynamic_zone ? 'true' : 'false'}
                    data-t-zonelayout={
                        selectedZone?.data?.body?.[0]?.primary?.layout
                            ? COLLECTION_ZONE_LAYOUT[
                                  selectedZone?.data?.body?.[0]?.primary?.layout as PRISMIC_ZONE_LAYOUT
                              ]
                            : ''
                    }>
                    {children[selectedElementIndex]}
                </div>
            )}
        </div>
    );
};
